import { useMemo } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { getClientStatus } from 'api/rest/analytics';
import { caseStatusOptions } from 'data/constants/caseStatus';
import InfoTooltip from 'components/v2/common/infoTooltip';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import {
    SectionCard,
    SectionTitle,
    SectionSubtitle,
    AnalyticsColors,
    loadingWrapSx,
    stateMessageSx,
} from '../../components/analyticsUi';
import { OPERATIONS_CLIENT_STATUS } from '../../constants/metricDescriptions';
import type { ClientStatusCountItem } from 'hooks/analytics';

interface ClientStatusSectionProps {
    from?: string;
    to?: string;
    serviceProviderId?: string;
}

const STATUS_META = new Map(
    caseStatusOptions.map((o) => [o.value, { label: o.label, color: o.color }])
);

/** Canonical display order matching caseStatusOptions (pipeline order) */
const STATUS_ORDER = caseStatusOptions.map((o) => o.value);

const formatStatusLabel = (status: string): string =>
    STATUS_META.get(status)?.label ??
    status
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

const statusColor = (status: string): string =>
    STATUS_META.get(status)?.color ?? AnalyticsColors.primaryMuted;

const sortStatuses = (items: ClientStatusCountItem[]): ClientStatusCountItem[] => {
    const orderIndex = new Map(STATUS_ORDER.map((s, i) => [s, i]));
    return [...items].sort((a, b) => {
        const ai = orderIndex.get(a.status) ?? 999;
        const bi = orderIndex.get(b.status) ?? 999;
        if (ai !== bi) return ai - bi;
        return a.status.localeCompare(b.status);
    });
};

const ClientStatusSection = ({ from, to, serviceProviderId }: ClientStatusSectionProps) => {
    const { data, loading, error } = useAnalyticsData(
        () => getClientStatus(from, to, serviceProviderId),
        [from, to, serviceProviderId]
    );

    const rows = useMemo(() => {
        if (!data) return [];
        const list =
            data.statuses?.length > 0
                ? data.statuses
                : Object.entries(data.clientStatusCounts ?? {}).map(([status, count]) => ({
                      status,
                      count,
                  }));
        return sortStatuses(list);
    }, [data]);

    const maxCount = useMemo(() => Math.max(...rows.map((r) => r.count), 1), [rows]);
    const total = data?.total ?? rows.reduce((sum, r) => sum + r.count, 0);
    const nonZeroCount = rows.filter((r) => r.count > 0).length;

    return (
        <SectionCard>
            <SectionTitle>
                File Status
                <InfoTooltip title={OPERATIONS_CLIENT_STATUS} />
            </SectionTitle>
            <SectionSubtitle>
                Applications that entered each status during the selected period
            </SectionSubtitle>

            {loading && (
                <Box sx={loadingWrapSx}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!loading && error && <Box sx={stateMessageSx}>{error}</Box>}

            {!loading && !error && rows.length === 0 && (
                <Box sx={stateMessageSx}>No status transitions in this period</Box>
            )}

            {!loading && !error && rows.length > 0 && (
                <Box sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '0.5rem',
                            mb: '1.25rem',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Box
                            component="span"
                            sx={{ fontSize: 28, fontWeight: 700, color: AnalyticsColors.primary }}
                        >
                            {total}
                        </Box>
                        <Box
                            component="span"
                            sx={{ fontSize: 13, color: AnalyticsColors.textSecondary }}
                        >
                            status transitions
                            {nonZeroCount > 0 && (
                                <Box
                                    component="span"
                                    sx={{ color: AnalyticsColors.textTertiary, fontWeight: 500 }}
                                >
                                    {` · ${nonZeroCount} active status${nonZeroCount === 1 ? '' : 'es'}`}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {rows.map((row) => {
                        const label = formatStatusLabel(row.status);
                        const color = statusColor(row.status);
                        const pct = Math.max((row.count / maxCount) * 100, row.count > 0 ? 4 : 0);
                        const share = total > 0 ? Math.round((row.count / total) * 100) : 0;

                        return (
                            <Tooltip
                                key={row.status}
                                title={`${label}: ${row.count} application${row.count === 1 ? '' : 's'} entered this status${total > 0 ? ` (${share}% of transitions)` : ''}`}
                                arrow
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        mb: '8px',
                                        borderRadius: '6px',
                                        py: '2px',
                                        opacity: row.count === 0 ? 0.45 : 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 148,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: AnalyticsColors.textSecondary,
                                            textAlign: 'right',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {label}
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            height: 24,
                                            background: '#F3F4F6',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            position: 'relative',
                                        }}
                                    >
                                        {row.count > 0 && (
                                            <Box
                                                sx={{
                                                    height: '100%',
                                                    width: `${pct}%`,
                                                    background: color,
                                                    borderRadius: '6px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    pl: '8px',
                                                    transition: 'width 0.4s ease',
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: 'white',
                                                        whiteSpace: 'nowrap',
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                                                    }}
                                                >
                                                    {row.count}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 44,
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: AnalyticsColors.textPrimary,
                                            textAlign: 'right',
                                            flexShrink: 0,
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        {row.count}
                                    </Box>
                                </Box>
                            </Tooltip>
                        );
                    })}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            pt: '10px',
                            mt: '6px',
                            borderTop: `1px solid ${AnalyticsColors.borderLight}`,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: AnalyticsColors.textSecondary,
                            }}
                        >
                            {rows.length} statuses tracked
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: AnalyticsColors.textPrimary,
                            }}
                        >
                            {total} total
                        </Box>
                    </Box>
                </Box>
            )}
        </SectionCard>
    );
};

export default ClientStatusSection;
